// pages/AdminPage.jsx — Admin CRUD panel
// KEY FIX: Field, FirmPickerButton, Toggle, LatexPreview are defined
// OUTSIDE AdminPage so they are stable references. Defining them inside
// caused React to unmount/remount the input on every keystroke (focus loss bug).

import React, { useState, useEffect, useCallback } from "react";
import { TOPICS, DIFFICULTIES, DIFFICULTY_META, COMPANY_META, PROBLEMS_PER_PAGE } from "../data/constants";
import { useTheme }   from "../context/ThemeContext";
import * as api       from "../services/api";
import DiffPip        from "../components/atoms/DiffPip";
import { TopicBadge } from "../components/atoms/Badges";
import FirmChip       from "../components/atoms/FirmChip";
import Pagination     from "../components/atoms/Pagination";
import LatexRenderer  from "../components/atoms/LatexRenderer";

// ── Field — defined OUTSIDE parent so ref is stable across renders ───
// If defined inside, every parent re-render creates a new component type,
// React unmounts the old one (losing focus) and mounts fresh.
function Field({ k, label, ph, area, latex, value, onChange }) {
  const { t } = useTheme();
  const base = {
    width: "100%", boxSizing: "border-box",
    border: `0.5px solid ${t.border2}`, borderRadius: 8,
    fontSize: 13, color: t.text, fontFamily: "inherit",
    outline: "none", background: t.inputBg,
  };
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>{label}</label>
      {area
        ? <textarea value={value} onChange={onChange} placeholder={ph}
            style={{ ...base, minHeight: 90, padding: "9px 12px", resize: "vertical", lineHeight: 1.6 }} />
        : <input type="text" value={value} onChange={onChange} placeholder={ph}
            style={{ ...base, height: 37, padding: "0 12px" }} />
      }
      {latex && value && <LatexPreview text={value} />}
    </div>
  );
}

// ── LatexPreview — also outside ──────────────────────────────────────
function LatexPreview({ text }) {
  const { t } = useTheme();
  return (
    <div style={{ background: t.bg, border: `0.5px solid ${t.border}`, borderRadius: 8, padding: "10px 14px", marginTop: 6 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 6px" }}>Preview</p>
      <div style={{ fontSize: 14, lineHeight: 1.9 }}><LatexRenderer text={text} /></div>
    </div>
  );
}

// ── FirmPickerButton — outside ────────────────────────────────────────
function FirmPickerButton({ name, meta, selected, onToggle }) {
  const { t } = useTheme();
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <button onClick={onToggle} title={name} style={{
      display: "flex", alignItems: "center", gap: 7,
      padding: "5px 10px 5px 6px", borderRadius: 8,
      border: `0.5px solid ${selected ? "#6b4fe0" : t.border}`,
      background: selected ? "rgba(107,79,224,0.1)" : t.cardBg,
      cursor: "pointer", transition: "all 0.15s",
    }}>
      {!imgFailed && meta.logo
        ? <img src={meta.logo} alt={name} onError={() => setImgFailed(true)}
            style={{ width: 18, height: 18, borderRadius: 4, objectFit: "contain", background: "#fff", flexShrink: 0 }} />
        : <div style={{ width: 18, height: 18, borderRadius: 4, background: meta.bg, color: meta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 6, fontWeight: 700, fontFamily: "monospace", flexShrink: 0 }}>{meta.abbr}</div>
      }
      <span style={{ fontSize: 12, fontWeight: selected ? 600 : 400, color: selected ? "#6b4fe0" : t.text2 }}>{name}</span>
      {selected && <span style={{ fontSize: 9, color: "#6b4fe0", marginLeft: 2 }}>✓</span>}
    </button>
  );
}

// ── Toggle — outside ─────────────────────────────────────────────────
function Toggle({ on, onToggle, label, sub }) {
  const { t } = useTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: t.bg2, borderRadius: 10, border: `0.5px solid ${t.border}` }}>
      <button onClick={onToggle} style={{ width: 42, height: 22, borderRadius: 11, border: "none", cursor: "pointer", background: on ? "#2d7a4f" : t.border2, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: on ? 22 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
      </button>
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: on ? "#2d7a4f" : "inherit", margin: "0 0 1px" }}>{label}</p>
        <p style={{ fontSize: 11, color: "inherit", opacity: 0.55, margin: 0 }}>{sub}</p>
      </div>
    </div>
  );
}

// ════════════════════════ MAIN COMPONENT ═════════════════════════════
export default function AdminPage({ onRefresh }) {
  const { t } = useTheme();

  const [problems, setProblems] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [pg,       setPg]       = useState(1);
  const [listLoad, setListLoad] = useState(true);

  const [search, setSearch] = useState("");
  const [topicF, setTopicF] = useState("All");
  const [diffF,  setDiffF]  = useState("All");
  const [firmF,  setFirmF]  = useState("All");

  const [view,   setView]   = useState("list");
  const [editId, setEditId] = useState(null);
  const [saved,  setSaved]  = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [busy,   setBusy]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const blank = {
    problemId: "", title: "", topic: "Probability", difficulty: "Medium",
    tags: [], tagInput: "", askedIn: [],
    preview: "", fullStatement: "", approach: "", solution: "",
    answer: "",
    adminRating: 3.0, solutionRevealed: true,
  };
  const [form, setForm] = useState(blank);

  // ── field updater — stable, doesn't re-create Field component ────
  const setField = useCallback((k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value })), []);

  const fetchList = useCallback(async () => {
    setListLoad(true);
    try {
      const params = { page: pg, limit: PROBLEMS_PER_PAGE };
      if (search)           params.search     = search;
      if (topicF !== "All") params.topic      = topicF;
      if (diffF  !== "All") params.difficulty = diffF;
      if (firmF  !== "All") params.firm       = firmF;
      const data = await api.getProblems(params);
      setProblems(data.problems);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error("Admin fetch:", err.message); }
    finally { setListLoad(false); }
  }, [pg, search, topicF, diffF, firmF]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const allFirms  = ["All", ...Object.keys(COMPANY_META)];
  const setF      = (fn, v) => { fn(v); setPg(1); };
  const addTag    = () => { const tg = form.tagInput.trim(); if (tg && !form.tags.includes(tg)) setForm((f) => ({ ...f, tags: [...f.tags, tg], tagInput: "" })); };
  const removeTag = (tg) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== tg) }));
  const toggleFirm = (name) => setForm((f) => ({ ...f, askedIn: f.askedIn.includes(name) ? f.askedIn.filter((x) => x !== name) : [...f.askedIn, name] }));

  const handleSave = async () => {
    if (!form.title.trim()) { setApiErr("Title is required."); return; }
    if (!form.answer.trim()) { setApiErr("Answer is required."); return; }
    setBusy(true); setApiErr("");
    try {
      const payload = { ...(form.problemId.trim() ? { problemId: form.problemId.trim() } : {}), title: form.title, topic: form.topic, difficulty: form.difficulty, tags: form.tags, askedIn: form.askedIn, preview: form.preview, fullStatement: form.fullStatement, approach: form.approach, solution: form.solution, answer: form.answer.trim(), adminRating: parseFloat(form.adminRating) || 3.0, solutionRevealed: form.solutionRevealed };
      if (editId) await api.updateProblem(editId, payload);
      else        await api.createProblem(payload);
      setForm(blank); setEditId(null); setView("list");
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      fetchList(); onRefresh?.();
    } catch (err) { setApiErr(err.message); }
    finally { setBusy(false); }
  };

  const handleDelete = (problemId) => {
    setDeleteTarget(problemId);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setDeleteError("");
    try {
      await api.deleteProblem(deleteTarget);
      setDeleteTarget(null);
      fetchList();
      onRefresh?.();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleReveal = async (p) => {
    try { await api.updateProblem(p.problemId, { solutionRevealed: !p.solutionRevealed }); fetchList(); }
    catch (err) { alert("Update failed: " + err.message); }
  };

  const startEdit = async (p) => {
    setBusy(true);
    setApiErr("");
    try {
      const data = await api.getAdminProblem(p.problemId);
      setForm({ ...data.problem, problemId: data.problem.problemId, tagInput: "", answer: data.problem.answer || "", adminRating: data.problem.adminRating ?? 3.0 });
      setEditId(p.problemId); setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setApiErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  const selStyle = { height: 34, padding: "0 10px", border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, background: t.inputBg, cursor: "pointer", outline: "none", fontFamily: "inherit", flexShrink: 0 };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "84px 32px 80px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Admin Panel</span>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: t.text, margin: 0, fontFamily: "'Playfair Display',Georgia,serif" }}>
            {view === "form" ? (editId ? "Edit Problem" : "New Problem") : "Problem Management"}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {saved && <span style={{ fontSize: 12, color: "#2d7a4f", fontWeight: 600 }}>✓ Saved</span>}
          {view === "list" && (
            <>
              <div style={{ background: t.bg2, borderRadius: 8, padding: "7px 14px" }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: t.text }}>{total}</span>
                <span style={{ fontSize: 12, color: t.text3, marginLeft: 5 }}>problems</span>
              </div>
              <button onClick={() => { setForm(blank); setEditId(null); setApiErr(""); setView("form"); }}
                style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                + Add Problem
              </button>
            </>
          )}
          {view === "form" && (
            <button onClick={() => { setView("list"); setForm(blank); setEditId(null); }}
              style={{ background: "transparent", color: t.text3, border: `0.5px solid ${t.border}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* ═══════════════ FORM ═══════════════ */}
      {view === "form" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

          {/* Problem ID + Title */}
          {editId ? (
            <Field k="problemId" label="PROBLEM ID" ph="e.g. QS-026" value={form.problemId} onChange={setField("problemId")} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 64, padding: "0 14px", background: t.bg2, border: `0.5px solid ${t.border}`, borderRadius: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em" }}>PROBLEM ID</span>
              <span style={{ fontSize: 13, color: t.text3, marginTop: 6 }}>Automatically assigned on publish</span>
            </div>
          )}
          <Field k="title"     label="TITLE"      ph="e.g. Gambler's Ruin…"               value={form.title}     onChange={setField("title")} latex />

          {/* Left: topic / difficulty / rating */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>TOPIC</label>
              <select value={form.topic} onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                style={{ width: "100%", ...selStyle, height: 37 }}>
                {TOPICS.slice(1).map((tp) => <option key={tp}>{tp}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>DIFFICULTY</label>
              <div style={{ display: "flex", gap: 6 }}>
                {DIFFICULTIES.slice(1).map((d) => (
                  <button key={d} onClick={() => setForm((f) => ({ ...f, difficulty: d }))}
                    style={{ flex: 1, padding: "7px 0", borderRadius: 7, border: `0.5px solid ${form.difficulty === d ? DIFFICULTY_META[d].dot : t.border}`, background: form.difficulty === d ? DIFFICULTY_META[d].bg : t.cardBg, color: form.difficulty === d ? DIFFICULTY_META[d].color : t.text2, fontSize: 11, fontWeight: form.difficulty === d ? 700 : 400, cursor: "pointer" }}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>INITIAL RATING (1–5)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input type="range" min="1" max="5" step="0.1" value={form.adminRating}
                  onChange={(e) => setForm((f) => ({ ...f, adminRating: parseFloat(e.target.value) }))}
                  className="rating-range"
                  style={{ flex: 1, background: `linear-gradient(to right, #6b4fe0 ${((form.adminRating - 1) / 4) * 100}%, ${t.border2} ${((form.adminRating - 1) / 4) * 100}%)` }} />
                <div style={{ background: "#6b4fe0", color: "#fff", borderRadius: 9, padding: "8px 14px", fontSize: 16, fontWeight: 700, minWidth: 48, textAlign: "center" }}>
                  {parseFloat(form.adminRating).toFixed(1)}
                </div>
              </div>
              <style>{`
                .rating-range { appearance: none; height: 8px; border-radius: 999px; outline: none; cursor: pointer; }
                .rating-range::-webkit-slider-thumb { appearance: none; width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 1px solid ${t.border}; box-shadow: 0 2px 8px rgba(15,17,23,0.18); }
                .rating-range::-moz-range-thumb { width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 1px solid ${t.border}; box-shadow: 0 2px 8px rgba(15,17,23,0.18); }
              `}</style>
            </div>
          </div>

          {/* Right: tags + firms */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>TAGS</label>
              <div style={{ display: "flex", gap: 7, marginBottom: 8 }}>
                <input
                  type="text" value={form.tagInput}
                  onChange={(e) => setForm((f) => ({ ...f, tagInput: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                  placeholder="Type tag, press Enter"
                  style={{ flex: 1, height: 35, padding: "0 11px", border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, fontFamily: "inherit", outline: "none", background: t.inputBg }}
                />
                <button onClick={addTag} style={{ padding: "0 12px", background: t.primary, color: t.primaryFg, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Add</button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {form.tags.map((tg) => (
                  <span key={tg} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: t.tag, borderRadius: 20, padding: "3px 10px", fontSize: 12, color: t.tagText }}>
                    {tg}
                    <button onClick={() => removeTag(tg)} style={{ background: "none", border: "none", cursor: "pointer", color: t.text3, fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
                {form.tags.length === 0 && <span style={{ fontSize: 12, color: t.text3 }}>No tags yet</span>}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 8 }}>ASKED IN (OPTIONAL)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries(COMPANY_META).map(([name, meta]) => (
                  <FirmPickerButton key={name} name={name} meta={meta} selected={form.askedIn.includes(name)} onToggle={() => toggleFirm(name)} />
                ))}
              </div>
            </div>
          </div>

          {/* Full-width text fields */}
          <div style={{ gridColumn: "1/-1", display: "flex", flexDirection: "column", gap: 18 }}>
            <Field k="preview"       label="SHORT PREVIEW (OPTIONAL — plain text, shown on card)" ph="One-sentence summary…" value={form.preview}       onChange={setField("preview")} />
            <Field k="fullStatement" label="FULL STATEMENT — use $...$ inline, $$...$$ block for math"                  ph="Full problem text with LaTeX math…" value={form.fullStatement} onChange={setField("fullStatement")} area latex />
            <Field k="approach"      label="APPROACH / HINT (LaTeX supported)"         ph="Key insight or method…"        value={form.approach}      onChange={setField("approach")} area latex />
            <Field k="solution"      label="SOLUTION (LaTeX supported)"                ph="Complete worked solution…"     value={form.solution}      onChange={setField("solution")} area latex />
            <Field k="answer"        label="ANSWER (required — used for future POTD validation)" ph="Canonical answer for scoring…" value={form.answer} onChange={setField("answer")} />
          </div>

          {/* Solution reveal */}
          <div style={{ gridColumn: "1/-1" }}>
            <Toggle
              on={form.solutionRevealed}
              onToggle={() => setForm((f) => ({ ...f, solutionRevealed: !f.solutionRevealed }))}
              label={form.solutionRevealed ? "Solution visible to users" : "Solution hidden from users"}
              sub="Toggle any time from the problem list."
            />
          </div>

          {/* Submit */}
          <div style={{ gridColumn: "1/-1", display: "flex", flexDirection: "column", gap: 10 }}>
            {apiErr && <p style={{ fontSize: 12, color: "#c0392b", margin: 0 }}>{apiErr}</p>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleSave} disabled={busy}
                style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 9, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}>
                {busy ? "Saving…" : editId ? "Save Changes" : "Publish Problem"}
              </button>
              <button onClick={() => { setView("list"); setForm(blank); setEditId(null); }}
                style={{ background: "transparent", color: t.text3, border: `0.5px solid ${t.border}`, borderRadius: 9, padding: "12px 18px", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ LIST ═══════════════ */}
      {view === "list" && (
        <>
          {/* Filter bar */}
          <div style={{ background: t.bg2, border: `0.5px solid ${t.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "1 1 200px" }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: t.text3 }}>
                  <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input type="text" placeholder="Search title, ID, tag…" value={search}
                  onChange={(e) => setF(setSearch, e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", paddingLeft: 30, height: 34, border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, background: t.inputBg, outline: "none", fontFamily: "inherit" }} />
              </div>
              <select value={topicF} onChange={(e) => setF(setTopicF, e.target.value)} style={selStyle}>
                {["All", ...TOPICS.slice(1)].map((tp) => <option key={tp}>{tp}</option>)}
              </select>
              <select value={diffF}  onChange={(e) => setF(setDiffF, e.target.value)} style={selStyle}>
                {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
              </select>
              <select value={firmF}  onChange={(e) => setF(setFirmF, e.target.value)} style={selStyle}>
                {allFirms.map((f) => <option key={f}>{f}</option>)}
              </select>
              {(search || topicF !== "All" || diffF !== "All" || firmF !== "All") && (
                <button onClick={() => { setSearch(""); setTopicF("All"); setDiffF("All"); setFirmF("All"); setPg(1); }}
                  style={{ padding: "0 12px", height: 34, background: "transparent", color: "#c0392b", border: "0.5px solid #c0392b", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  Clear
                </button>
              )}
            </div>
            <p style={{ fontSize: 12, color: t.text3, margin: 0 }}>{total} problem{total !== 1 ? "s" : ""} · page {pg}/{pages}</p>
          </div>

          {/* Table */}
          <div style={{ background: t.cardBg, border: `0.5px solid ${t.border}`, borderRadius: 13, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", borderBottom: `0.5px solid ${t.border}`, background: t.bg2 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.text2 }}>
                {(pg-1)*PROBLEMS_PER_PAGE+1}–{Math.min(pg*PROBLEMS_PER_PAGE, total)} of {total}
              </span>
            </div>
            {listLoad ? (
              <div style={{ padding: "40px", textAlign: "center", color: t.text3 }}>Loading…</div>
            ) : problems.length === 0 ? (
              <div style={{ padding: "48px", textAlign: "center", color: t.text3 }}><p>No problems match your filters.</p></div>
            ) : problems.map((p, i) => (
              <div key={p.problemId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: i < problems.length-1 ? `0.5px solid ${t.border}` : "none" }}>
                <span style={{ fontSize: 10, color: t.text3, fontFamily: "monospace", width: 56, flexShrink: 0 }}>{p.problemId}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: t.text, margin: "0 0 3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</p>
                  <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    <DiffPip level={p.difficulty} />
                    <TopicBadge label={p.topic} />
                    <span style={{ fontSize: 11, color: t.text3 }}>★ {p.peerRating ? `${p.peerRating.toFixed(1)} peer` : `${(p.adminRating||3).toFixed(1)} admin`}</span>
                    {p.addedBy?.username && <span style={{ fontSize: 11, color: t.text3 }}>· added by {p.addedBy.username}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  {(p.askedIn||[]).map((f) => <FirmChip key={f} name={f} />)}
                </div>
                <button onClick={() => toggleReveal(p)}
                  style={{ padding: "3px 9px", borderRadius: 6, border: "0.5px solid", borderColor: p.solutionRevealed ? "#2d7a4f" : t.border, background: p.solutionRevealed ? "#edf7f1" : t.bg2, color: p.solutionRevealed ? "#2d7a4f" : t.text3, fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
                  {p.solutionRevealed ? "Revealed" : "Hidden"}
                </button>
                <button onClick={() => startEdit(p)}
                  style={{ padding: "3px 9px", borderRadius: 6, border: `0.5px solid ${t.border}`, background: t.cardBg, color: t.text2, fontSize: 11, cursor: "pointer", flexShrink: 0 }}>
                  Edit
                </button>
                <button onClick={() => handleDelete(p.problemId)}
                  style={{ width: 26, height: 26, borderRadius: 6, border: `0.5px solid ${t.border}`, background: t.cardBg, color: "#c0392b", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  ×
                </button>
              </div>
            ))}
          </div>
          <Pagination cur={pg} total={pages} onChange={(p) => { setPg(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        </>
      )}

      {deleteTarget && (
        <div
          role="presentation"
          onClick={() => { if (!busy) setDeleteTarget(null); }}
          style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(15,17,23,0.52)", backdropFilter: "blur(5px)" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(100%, 420px)", background: t.cardBg, border: `0.5px solid ${t.border}`, borderRadius: 14, padding: 26, boxShadow: "0 18px 60px rgba(0,0,0,0.25)" }}
          >
            <div style={{ width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, background: "rgba(192,57,43,0.12)", color: "#c0392b", fontSize: 19, fontWeight: 700, marginBottom: 16 }}>!</div>
            <h2 id="delete-dialog-title" style={{ fontSize: 20, color: t.text, margin: "0 0 8px", fontFamily: "'Playfair Display',Georgia,serif" }}>Delete {deleteTarget}?</h2>
            <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, margin: "0 0 20px" }}>This problem and its ratings will be permanently deleted. This action cannot be undone.</p>
            {deleteError && <p style={{ fontSize: 12, color: "#c0392b", margin: "0 0 14px" }}>{deleteError}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
              <button onClick={() => setDeleteTarget(null)} disabled={busy}
                style={{ background: "transparent", color: t.text2, border: `0.5px solid ${t.border}`, borderRadius: 8, padding: "9px 15px", fontSize: 13, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={busy}
                style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: 8, padding: "9px 15px", fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}>
                {busy ? "Deleting..." : "Delete Problem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
